import DOMPurify, { type Config } from 'dompurify';

const domPurify = DOMPurify();

// Keep `style` but restrict it to the value-safe presentational declarations rich-text content
// carries: text/background colour and image dimensions from the resize handle, plus the table and
// alignment styling the editor emits (see rich-text `table-kit`) and pasted content brings.
// Preserving these keeps the read-only viewer and PDF export rendering tables exactly like the editor.
//
// The list deliberately excludes the `background` and `border-image` shorthands (both accept
// url()) and layout-breaking props (display/position/float/margin/font-family) so foreign content
// (Office paste, legacy data) still can't override the controlled rich-text styling. Lives here
// rather than in the rich-text package so every InnerHTMLDiv consumer renders the same.
const ALLOWED_STYLE_DECLARATIONS =
  /^(color|background-color|width|height|text-align|vertical-align|font-weight|border|border-(top|right|bottom|left|color|width|style|collapse|spacing|radius)|padding|padding-(top|right|bottom|left))\s*:/i;

domPurify.addHook('uponSanitizeAttribute', (_node, data) => {
  if (data.attrName !== 'style') {
    return;
  }

  const safe = data.attrValue
    .split(';')
    .map((decl) => decl.trim())
    .filter((decl) => ALLOWED_STYLE_DECLARATIONS.test(decl))
    .join('; ');

  if (safe) {
    data.attrValue = safe;
  } else {
    data.keepAttr = false;
  }
});

domPurify.addHook('afterSanitizeAttributes', (node) => {
  if ('target' in node) {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer');
  }
});

export const sanitizeHtml = (value: string, overrides?: Config) => {
  const config: Config = {
    ...overrides,
    ADD_ATTR: ['target', 'rel'],
    // <font> is deprecated HTML still embedded in legacy data (Office paste etc.) and
    // hardcodes typography that bypasses the rich-text styling; <style> would leak CSS
    // rules as visible text once attributes are stripped. Strip both but keep inner
    // content for <font>.
    FORBID_TAGS: ['font', 'style'],
    USE_PROFILES: { html: true },
  };
  return domPurify.sanitize(value, config);
};
